import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineTimesCircleIcon from 'LuxKit/Icons/line/LineTimesCircleIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import LineThumbsDownIcon from 'LuxKit/Icons/line/LineThumbsDownIcon'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import Group from 'components/utils/Group'
import { RECOMMENDED_RATING_THRESHOLD } from 'constants/review'
import useReviewLabels from 'hooks/useReviewLabels'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import debounce from 'debounce-promise'
import UserReviewGallery from './UserReviewGallery'
import Image from 'components/Common/Image/Image'
import { getRecommendsLabel } from 'lib/order/reviewUtils'
import ModalContext from 'contexts/ModalContext'
import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { formatMiddleDotSeparator, middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import {
  INTL_DMY_CASUAL_FORMAT,
  INTL_SHORT_MONTH_LONG_YEAR,
} from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { getReviewHighlightParts } from 'lib/reviews/ratingBreakdownUtils'
import {
  REVIEW_SECTION_CONTAINER_NAME,
  REVIEW_SECTION_WIDE_BREAKPOINT,
} from './reviewLayout'

function getPrimaryLanguageCode(locale?: string) {
  return locale?.split(',')[0]?.trim().split(/[-_]/)[0]?.toLowerCase()
}

const Root = styled.div<{ $useContainerLayout: boolean }>`
  display: flex;
  flex-direction: column;
  padding-top: ${rem(24)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  > *:nth-child(2) {
    overflow: hidden;
  }

  ${({ $useContainerLayout }) => $useContainerLayout ? css`
    @container ${REVIEW_SECTION_CONTAINER_NAME} (min-width: ${rem(REVIEW_SECTION_WIDE_BREAKPOINT)}) {
      flex-direction: row;
      gap: ${rem(20)};

      > *:first-child {
        width: ${rem(200)};
      }
    }
  ` : css`
    ${mediaQueryUp.tablet} {
      flex-direction: row;
      gap: ${rem(20)};

      > *:first-child {
        width: ${rem(200)};
      }
    }
  `}
`

const EmbeddedNarrowRating = styled.div`
  display: block;

  @container ${REVIEW_SECTION_CONTAINER_NAME} (min-width: ${rem(REVIEW_SECTION_WIDE_BREAKPOINT)}) {
    display: none;
  }
`

const EmbeddedWideRating = styled.div`
  display: none;

  @container ${REVIEW_SECTION_CONTAINER_NAME} (min-width: ${rem(REVIEW_SECTION_WIDE_BREAKPOINT)}) {
    display: block;
  }
`

const ReviewDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const ReviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  flex: 1;
`

const TagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
  margin-top: ${rem(8)};
`

const ResponseContainer = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  padding: ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  ${mediaQueryUp.tablet} {
    grid-column: 1 / -1;
  }
`

const LELogo = styled(LuxuryEscapesSmallLogo)`
  fill: ${props => props.theme.palette.neutral.surface.darkForeground};
  width: ${rem(20)};
`

const WhitelabelLogoWrapper = styled.div`
  display: flex;
  svg {
    max-width: 60px;
  }
`

const Chip = styled.div`
  display: flex;
  align-items: center;
  height: ${rem(28)};
  padding: 0 ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.surface.lightForeground};
`

const ImagesContainer = styled(Group)`
  margin-top: ${rem(16)};
  padding-bottom: ${rem(8)};
  overflow: auto;
`

const ImageWrapper = styled(Clickable)`
  position: relative;
  width: ${rem(88)};
  height: ${rem(88)};
  min-width: ${rem(88)};
  min-height: ${rem(88)};
`

const CompactReviewContainer = styled(Group)`
  padding: ${rem(16)};
  height: ${rem(240)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const CompactReviewContent = styled.div`
  position: relative;
  max-height: ${rem(120)};
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  line-clamp: 5;
  text-overflow: ellipsis;
`

const ReadMoreButton = styled(Clickable)`
  color: ${props => props.theme.palette.brand.primary.normal};
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
`

const PanelReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(40)} 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${rem(4)};
`

const PanelTripDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${rem(4)};
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

const PanelTripDetailItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
`

const PanelTripDetailDivider = styled.span`
  margin: 0 ${rem(4)};
`

const HighlightMark = styled.mark`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  color: inherit;
  padding: 0;
`

interface Props {
  userReview: App.Review
  className?: string
  showReviewFiltersAndUserDetails?: boolean
  variant?: 'default' | 'compact' | 'panel' | 'embedded'
  onOpenReviewsModal?: (reviewId: string) => void
  /** When true, the review content will be expanded by default */
  defaultExpanded?: boolean
  searchTerm?: string
}

function UserReviewTile(props: Props) {
  const {
    userReview,
    className,
    showReviewFiltersAndUserDetails = true,
    variant = 'default',
    onOpenReviewsModal,
    defaultExpanded,
    searchTerm = '',
  } = props
  const intl = useIntl()
  const { COMPANIONSHIP_ENUM, DEFINITION_ENUM } = useReviewLabels()
  const [contentHeight, setContentHeight] = useState<number>(0)
  const [isShowingOriginalReview, setIsShowingOriginalReview] = useState(false)
  const contentElement = useRef<HTMLDivElement>(null)

  const userCountry = config.regions.find(region => region.code === userReview.userCountry)
  const recommendsLabel = showReviewFiltersAndUserDetails && getRecommendsLabel(userReview, intl)
  const translatedReview = userReview.translations?.review
  const activeLanguageCode = getPrimaryLanguageCode(intl.locale)
  const targetLanguageCode = getPrimaryLanguageCode(translatedReview?.targetLanguage)
  const translatedReviewText = activeLanguageCode === targetLanguageCode ? translatedReview?.text : undefined
  const canToggleOriginalReview = !!translatedReviewText && !!userReview.review
  const displayedReviewText = isShowingOriginalReview ? userReview.review : translatedReviewText ?? userReview.review
  const sourceLanguageCode = getPrimaryLanguageCode(translatedReview?.sourceLanguage)
  const sourceLanguageName = useMemo(() => {
    if (!sourceLanguageCode || typeof Intl.DisplayNames !== 'function') return undefined

    return new Intl.DisplayNames(intl.locale, { type: 'language', fallback: 'code' }).of(sourceLanguageCode)
  }, [intl.locale, sourceLanguageCode])

  const escapesLabel = intl.formatMessage(
    {
      id: 'f7kwhj9dXNaUUhRIKAis',
      defaultMessage: '{count, plural, one {# escape} other {# escapes}}',
      description: 'Number of escapes a reviewer has booked on their user review tile',
    },
    { count: userReview.userOrdersCount ?? 0 },
  )
  const reviewsLabel = intl.formatMessage(
    {
      id: 'qP_ItzGy9t_Mahf_AonN',
      defaultMessage: '{count, plural, one {# review} other {# reviews}}',
      description: 'Number of reviews a reviewer has written on their user review tile',
    },
    { count: userReview.userReviewsCount ?? 0 },
  )
  const nightsLabel = intl.formatMessage(
    {
      id: 'Q-pubcja8FwPyNDVWfH6',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
      description: 'Number of nights stayed shown on a user review tile',
    },
    { count: userReview.orderNumberOfNights ?? 0 },
  )

  const escapesAndReviewsLabel = useMemo(() => {
    const hasEscapes = !!userReview.userOrdersCount
    const hasReviews = !!userReview.userReviewsCount
    if (hasEscapes && hasReviews) {
      return intl.formatMessage(
        {
          id: 'EE8e2nPxOmqA1h3BiPBE',
          defaultMessage: '{escapes} · {reviews}',
          description: 'Escapes and reviews count separated by a dot on a user review tile',
        },
        { escapes: escapesLabel, reviews: reviewsLabel },
      )
    }
    if (hasEscapes) return escapesLabel
    if (hasReviews) return reviewsLabel
    return undefined
  }, [intl, userReview.userOrdersCount, userReview.userReviewsCount, escapesLabel, reviewsLabel])

  const nightsAndDateLabel = useMemo(() => {
    const hasNights = !!userReview.orderNumberOfNights
    const checkinDate = userReview.stayDate ?? userReview.reservationCheckinDate
    const formattedDate = checkinDate ? intlDateFormatter(checkinDate, INTL_SHORT_MONTH_LONG_YEAR, intl.locale) : undefined
    if (hasNights && formattedDate) {
      return intl.formatMessage(
        {
          id: 'zJ6soLm7J_iiCETDZZWv',
          defaultMessage: '{nights} · {date}',
          description: 'Nights stayed and check-in date separated by a dot on a user review tile',
        },
        { nights: nightsLabel, date: formattedDate },
      )
    }
    if (hasNights) return nightsLabel
    if (formattedDate) return formattedDate
    return undefined
  }, [intl, userReview.orderNumberOfNights, userReview.stayDate, userReview.reservationCheckinDate, nightsLabel])

  const isAnonymousReview = userReview.userName === 'Anonymous'
  const isPartnerReview = userReview?.source === 'bedbank' || userReview?.source === 'agoda'
  const reviewerDisplayName = isAnonymousReview ? intl.formatMessage({
    id: 'Us6dR4r1vAQUGic6NX5R',
    defaultMessage: 'Anonymous',
    description: 'Display name shown for an anonymous customer review',
  }) : userReview.userName

  const panelUserInfoLabel = useMemo(() => {
    const showCountry = userCountry && !isAnonymousReview
    const countryName = showCountry ? userCountry.name : undefined
    const parts = [countryName, escapesAndReviewsLabel].filter(Boolean)
    if (parts.length === 0) return undefined
    return parts.join(formatMiddleDotSeparator(intl))
  }, [intl, userCountry, isAnonymousReview, escapesAndReviewsLabel])

  useEffect(() => {
    const updateHeight = () => {
      setContentHeight(contentElement.current?.clientHeight ?? 0)
    }
    const handleResize = debounce(updateHeight, 1000)

    updateHeight()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [displayedReviewText])

  const readMoreLabel = intl.formatMessage({
    id: 'wkeWYFJoiPvXpM7Nsu5k',
    defaultMessage: 'Read more',
    description: 'Clickable label to expand and read the full user review',
  })
  const readLessLabel = intl.formatMessage({
    id: 'Zd8p_rTKtLuJcQc5TSRB',
    defaultMessage: 'Read less',
    description: 'Clickable label to collapse and hide the full user review',
  })

  const isCompact = variant === 'compact'
  const isEmbedded = variant === 'embedded'
  const showLightbox = useContext(ModalContext)
  const reviewedDateLabel = intlDateFormatter(userReview.submittedDate ?? userReview.createdAt, INTL_DMY_CASUAL_FORMAT, intl.locale)
  const responseDateLabel = userReview.response?.createdAt ?
      intlDateFormatter(userReview.response.createdAt, INTL_DMY_CASUAL_FORMAT, intl.locale) :
    ''
  const showImageLightbox = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const defaultIndex = parseInt(e.currentTarget.dataset.index ?? '0')
    showLightbox(<UserReviewGallery review={userReview} initialIndex={defaultIndex} />)
  }, [showLightbox, userReview])
  const highlightedReviewContent = useMemo(() => {
    const reviewHighlightParts = getReviewHighlightParts(displayedReviewText, searchTerm)

    // Reviews can be in any language, so isolate them and let the text set its own direction
    return (
      <bdi>
        {reviewHighlightParts.map((part, index) => part.isHighlighted
          ? <HighlightMark key={index}>{part.text}</HighlightMark>
          : <React.Fragment key={index}>{part.text}</React.Fragment>)}
      </bdi>
    )
  }, [displayedReviewText, searchTerm])
  const translatedFromLabel = useMemo(() => {
    if (!translatedReviewText || isShowingOriginalReview) return undefined
    if (sourceLanguageCode === 'en') {
      return intl.formatMessage({
        id: 'I2GlM9pMyM0_m8vdOe1e',
        defaultMessage: 'Translated from English',
        description: 'Subtle attribution shown under a machine translated guest review originally written in English',
      })
    }
    if (!sourceLanguageName) return undefined

    return intl.formatMessage(
      {
        id: 'obCK1rTIfeh4ZUybmRi4',
        defaultMessage: 'Translated from {language}',
        description: 'Subtle attribution shown under a machine translated guest review with the source language name',
      },
      { language: sourceLanguageName },
    )
  }, [intl, isShowingOriginalReview, sourceLanguageCode, sourceLanguageName, translatedReviewText])
  const translationToggleLabel = useMemo(() => {
    if (!canToggleOriginalReview) return undefined

    return isShowingOriginalReview ?
        intl.formatMessage({
          id: 'lWtdV04NaJeLg06k9zkw',
          defaultMessage: 'See translation',
          description: 'Small link under an original guest review to switch back to the translated review text',
        }) :
        intl.formatMessage({
          id: 'rqrJC0GQOTfB-8vKE9gd',
          defaultMessage: 'See original',
          description: 'Small link under a translated guest review to show the original review text',
        })
  }, [canToggleOriginalReview, intl, isShowingOriginalReview])
  const onToggleOriginalReview = useCallback(() => {
    setIsShowingOriginalReview(isShowingOriginal => !isShowingOriginal)
  }, [])
  const translationMeta = (translatedFromLabel || translationToggleLabel) && (
    <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
      {translatedFromLabel && (
        <BodyText variant="small" colour="light-foreground-alt">
          {translatedFromLabel}
        </BodyText>
      )}
      {translatedFromLabel && translationToggleLabel && (
        <BodyText variant="small" colour="light-foreground-alt">
          {intl.formatMessage(middleDotSeparatorMessage)}
        </BodyText>
      )}
      {translationToggleLabel && (
        <Clickable onClick={onToggleOriginalReview} style={{ cursor: 'pointer' }}>
          <BodyText variant="small" weight="bold" colour="primary" underline>
            {translationToggleLabel}
          </BodyText>
        </Clickable>
      )}
    </Group>
  )

  const renderDefault = () => {
    return (
      <Root className={className} $useContainerLayout={isEmbedded}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <ReviewDetails>
            <div>
              <BodyText weight="bold" variant="large">{isPartnerReview && userCountry ? userCountry.name : reviewerDisplayName}</BodyText>
              {userCountry && !isAnonymousReview && !isPartnerReview && <BodyText variant="medium">{userCountry.name}</BodyText>}
              {!isAnonymousReview && escapesAndReviewsLabel && (
                <BodyText variant="medium">
                  {escapesAndReviewsLabel}
                </BodyText>
              )}
              <BodyText variant="medium" colour="light-foreground-alt">
                {intl.formatMessage(
                  {
                    id: 'iN90F48rLwQXarZl-_MV',
                    defaultMessage: 'Reviewed {date}',
                    description: 'Date when the review was written in the default review tile',
                  },
                  { date: reviewedDateLabel },
                )}
              </BodyText>
            </div>
            <div>
              {userReview.companionship && (
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  <LineUsersAltIcon size="S" />
                  <BodyText variant="small">{COMPANIONSHIP_ENUM[userReview.companionship]}</BodyText>
                </Group>
              )}
              {userReview.offerRoomName && (
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  <LineBedIcon size="S" />
                  <BodyText variant="small">{userReview.offerRoomName}</BodyText>
                </Group>
              )}
              {nightsAndDateLabel && (
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  <LineCalendarIcon size="S" />
                  <BodyText variant="small">
                    {nightsAndDateLabel}
                  </BodyText>
                </Group>
              )}
            </div>
          </ReviewDetails>
          {isEmbedded ? <EmbeddedNarrowRating>
            <div>
              <NumberRating onlyNumber variant="large" rating={userReview.rating} />
            </div>
          </EmbeddedNarrowRating> : <CSSBreakpoint max="mobile">
            <div>
              <NumberRating onlyNumber variant="large" rating={userReview.rating} />
            </div>
          </CSSBreakpoint>}
        </Group>
        <ReviewContent>
          <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
            <ReviewContent>
              {userReview.title && <BodyText variant="large" weight="bold"><bdi>{userReview.title}</bdi></BodyText>}
              <LargeContentToggle
                maxHeight={contentHeight > 240 ? 120 : 240}
                showMoreText={readMoreLabel}
                showLessText={readLessLabel}
                defaultOpen={defaultExpanded}
              >
                <div ref={contentElement}>
                  <BodyText variant="large">{highlightedReviewContent}</BodyText>
                </div>
              </LargeContentToggle>
              {translationMeta}
              {recommendsLabel && (
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  {userReview.rating >= RECOMMENDED_RATING_THRESHOLD ? <LineCheckCircleIcon size="XS" colour="success" /> : <LineTimesCircleIcon size="XS" />}
                  <BodyText variant="small" weight="bold">{recommendsLabel}</BodyText>
                </Group>
              )}
            </ReviewContent>

            {isEmbedded ? <EmbeddedWideRating>
              <div>
                <NumberRating onlyNumber variant="large" rating={userReview.rating} />
              </div>
            </EmbeddedWideRating> : <CSSBreakpoint min="tablet">
              <div>
                <NumberRating onlyNumber variant="large" rating={userReview.rating} />
              </div>
            </CSSBreakpoint>}
          </Group>

          {!!userReview.definition?.length && (
            <TagGroup>
              {userReview.definition.map(definition => (
                <Chip key={definition}>
                  <BodyText variant="small">{DEFINITION_ENUM[definition]}</BodyText>
                </Chip>
              ))}
            </TagGroup>
          )}

          {!!userReview.photos?.length && (
            <ImagesContainer direction="horizontal" gap={16}>
              {userReview.photos.map((photo, index) => (
                <ImageWrapper
                  key={photo.imageId}
                  data-index={index}
                  onClick={showImageLightbox}
                >
                  <Image
                    id={photo.imageId}
                    alt={photo.imageId}
                    width={88}
                    height={88}
                    fit="center"
                    quality="good"
                  />
                </ImageWrapper>
              ))}
            </ImagesContainer>
          )}

          {userReview.response &&
            <ResponseContainer>
              <Group direction="horizontal" verticalAlign="center" gap={4}>
                {config.BRAND === 'luxuryescapes' && <LELogo />}
                {config.BRAND !== 'luxuryescapes' &&
                  <WhitelabelLogoWrapper>
                    <LogoIcon />
                  </WhitelabelLogoWrapper>}
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage(
                    {
                      id: 'q8mNGpCChwNJlz3vIze3',
                      defaultMessage: 'Response from {title}',
                      description: 'Label for a response from the brand to a user review',
                    },
                    { title: config.title },
                  )}
                </BodyText>
              </Group>
              <BodyText variant="medium" colour="light-foreground-alt">{responseDateLabel}</BodyText>
              <BodyText variant="large" colour="light-foreground-alt">{userReview.response.message}</BodyText>
            </ResponseContainer>}
        </ReviewContent>
      </Root>
    )
  }

  const renderCompact = () => {
    return (
      <CompactReviewContainer className={className} direction="vertical" verticalAlign="space-between">
        <VerticalSpacer gap={16}>
          <Group direction="horizontal" horizontalAlign="space-between">
            <div>
              <BodyText weight="bold" variant="large">{isPartnerReview && userCountry ? userCountry.name : reviewerDisplayName}</BodyText>
              <BodyText variant="medium" colour="light-foreground-alt">
                {intl.formatMessage(
                  {
                    id: 'YAMEBDcx9QvyC0GdOAFL',
                    defaultMessage: 'Reviewed {date}',
                    description: 'Date when the review was written in the compact review tile',
                  },
                  { date: reviewedDateLabel },
                )}
              </BodyText>
            </div>
            <div>
              <NumberRating onlyNumber variant="large" rating={userReview.rating} />
            </div>
          </Group>
          <CompactReviewContent>
            <BodyText variant="large">{highlightedReviewContent}</BodyText>
            {translationMeta}
          </CompactReviewContent>
        </VerticalSpacer>
        {onOpenReviewsModal && userReview.review && (
          <ReadMoreButton
            onClick={() => {
              if (!userReview.id) {
                return
              }
              onOpenReviewsModal(userReview.id)
            }}
          >
            <BodyText variant="medium">{readMoreLabel}</BodyText>
          </ReadMoreButton>
        )}
      </CompactReviewContainer>
    )
  }

  const renderPanel = () => {
    return (
      <PanelReviewContainer className={className}>
        {/* Header row: User name, Recommends badge, Rating */}
        <PanelHeader>
          <Group direction="vertical">
            <BodyText weight="bold" variant="large">{isPartnerReview && userCountry ? userCountry.name : reviewerDisplayName}</BodyText>
            {showReviewFiltersAndUserDetails && panelUserInfoLabel && (
              <BodyText variant="small" colour="light-foreground-alt">
                {panelUserInfoLabel}
              </BodyText>
            )}
          </Group>
          <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
            {recommendsLabel && (
              <Group direction="vertical" verticalAlign="center" horizontalAlign="end">
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  {userReview.rating >= RECOMMENDED_RATING_THRESHOLD
                    ? <LineThumbsUpIcon size="XS" colour="highlight-primary-dark" />
                    : <LineThumbsDownIcon size="XS" colour="light-foreground-alt" />}
                  <BodyText variant="small" weight="bold">{recommendsLabel}</BodyText>
                </Group>
                <BodyText variant="small" colour="light-foreground-alt">
                  {intl.formatMessage(
                    {
                      id: '9trdilh8j0lMNW3W7Yx0',
                      defaultMessage: 'Reviewed {date}',
                      description: 'Date when the review was written in the panel review tile',
                    },
                    { date: reviewedDateLabel },
                  )}
                </BodyText>
              </Group>
            )}
            <NumberRating onlyNumber variant="large" rating={userReview.rating} />
          </Group>
        </PanelHeader>

        {/* Trip details row */}
        {showReviewFiltersAndUserDetails && (
          <PanelTripDetails>
            {userReview.companionship && (
              <PanelTripDetailItem>
                <LineUsersAltIcon size="XS" />
                <BodyText variant="small">{COMPANIONSHIP_ENUM[userReview.companionship]}</BodyText>
              </PanelTripDetailItem>
            )}
            {userReview.companionship && userReview.offerRoomName && <PanelTripDetailDivider>{intl.formatMessage(middleDotSeparatorMessage)}</PanelTripDetailDivider>}
            {userReview.offerRoomName && (
              <PanelTripDetailItem>
                <LineBedIcon size="XS" />
                <BodyText variant="small">{userReview.offerRoomName}</BodyText>
              </PanelTripDetailItem>
            )}
            {userReview.offerRoomName && nightsAndDateLabel && <PanelTripDetailDivider>{intl.formatMessage(middleDotSeparatorMessage)}</PanelTripDetailDivider>}
            {nightsAndDateLabel && (
              <PanelTripDetailItem>
                <LineCalendarIcon size="XS" />
                <BodyText variant="small">
                  {nightsAndDateLabel}
                </BodyText>
              </PanelTripDetailItem>
            )}
          </PanelTripDetails>
        )}

        {/* Review content */}
        <LargeContentToggle
          maxHeight={contentHeight > 240 ? 120 : 240}
          showMoreText={readMoreLabel}
          showLessText={readLessLabel}
          defaultOpen={defaultExpanded}
        >
          <div ref={contentElement}>
            <BodyText variant="large">{highlightedReviewContent}</BodyText>
          </div>
        </LargeContentToggle>
        {translationMeta}

        {/* Definition tags */}
        {!!userReview.definition?.length && (
          <TagGroup>
            {userReview.definition.map(definition => (
              <Chip key={definition}>
                <BodyText variant="small">{DEFINITION_ENUM[definition]}</BodyText>
              </Chip>
            ))}
          </TagGroup>
        )}

        {/* Photo thumbnails */}
        {!!userReview.photos?.length && (
          <ImagesContainer direction="horizontal" gap={16}>
            {userReview.photos.map((photo, index) => (
              <ImageWrapper
                key={photo.imageId}
                data-index={index}
                onClick={showImageLightbox}
              >
                <Image
                  id={photo.imageId}
                  alt={photo.imageId}
                  width={88}
                  height={88}
                  fit="center"
                  quality="good"
                />
              </ImageWrapper>
            ))}
          </ImagesContainer>
        )}
      </PanelReviewContainer>
    )
  }

  if (variant === 'panel') {
    return renderPanel()
  }
  return isCompact ? renderCompact() : renderDefault()
}

export default UserReviewTile
